import earcut from 'earcut';
import type { Dash } from '@vp/shared-types';
import { dashPattern, dashPolyline } from '../pathgeom';

/** Triangle output: flat xy positions. */
export type Tri = number[];

/**
 * Stroke a polyline into triangles with miter joins (bevel above miter limit), butt caps.
 * `out` receives [x, y, ...] triangle vertices.
 */
export function strokePolyline(pts: number[], width: number, closed: boolean, out: Tri, miterLimit = 4): void {
  const n = pts.length / 2;
  if (n < 2) return;
  const hw = width / 2;
  // dedupe consecutive points
  const p: number[] = [pts[0], pts[1]];
  for (let i = 1; i < n; i++) {
    const x = pts[i * 2];
    const y = pts[i * 2 + 1];
    if (x !== p[p.length - 2] || y !== p[p.length - 1]) p.push(x, y);
  }
  if (closed && p.length >= 6 && p[0] === p[p.length - 2] && p[1] === p[p.length - 1]) p.length -= 2;
  const m = p.length / 2;
  if (m < 2) return;
  const segs = closed ? m : m - 1;
  // segment normals
  const nx: number[] = [];
  const ny: number[] = [];
  for (let i = 0; i < segs; i++) {
    const j = (i + 1) % m;
    const dx = p[j * 2] - p[i * 2];
    const dy = p[j * 2 + 1] - p[i * 2 + 1];
    const len = Math.hypot(dx, dy) || 1;
    nx.push(-dy / len);
    ny.push(dx / len);
  }
  for (let i = 0; i < segs; i++) {
    const j = (i + 1) % m;
    const ax = p[i * 2], ay = p[i * 2 + 1], bx = p[j * 2], by = p[j * 2 + 1];
    const ox = nx[i] * hw, oy = ny[i] * hw;
    out.push(ax + ox, ay + oy, bx + ox, by + oy, bx - ox, by - oy);
    out.push(ax + ox, ay + oy, bx - ox, by - oy, ax - ox, ay - oy);
  }
  // joins
  const joinCount = closed ? m : m - 2;
  for (let k = 0; k < joinCount; k++) {
    const i0 = closed ? (k - 1 + segs) % segs : k; // incoming segment
    const i1 = closed ? k : k + 1; // outgoing segment
    const vi = closed ? k : k + 1; // join vertex
    const vx = p[vi * 2], vy = p[vi * 2 + 1];
    const cross = nx[i0] * ny[i1] - ny[i0] * nx[i1];
    if (Math.abs(cross) < 1e-9 && nx[i0] * nx[i1] + ny[i0] * ny[i1] > 0) continue; // collinear
    // outer side sign
    const s = cross > 0 ? -1 : 1;
    const ax = vx + nx[i0] * hw * s, ay = vy + ny[i0] * hw * s;
    const bx = vx + nx[i1] * hw * s, by = vy + ny[i1] * hw * s;
    out.push(vx, vy, ax, ay, bx, by); // bevel
    let mx = nx[i0] + nx[i1];
    let my = ny[i0] + ny[i1];
    const ml = Math.hypot(mx, my);
    if (ml < 1e-9) continue;
    mx /= ml;
    my /= ml;
    const cosHalf = mx * nx[i0] + my * ny[i0];
    if (cosHalf <= 0 || 1 / cosHalf > miterLimit) continue;
    const L = hw / cosHalf;
    out.push(ax, ay, vx + mx * L * s, vy + my * L * s, bx, by);
  }
}

/** Filled disc approximated by a fan (used for dotted strokes). */
export function disc(cx: number, cy: number, r: number, out: Tri, segments = 8): void {
  for (let i = 0; i < segments; i++) {
    const a0 = (i / segments) * Math.PI * 2;
    const a1 = ((i + 1) / segments) * Math.PI * 2;
    out.push(cx, cy, cx + Math.cos(a0) * r, cy + Math.sin(a0) * r, cx + Math.cos(a1) * r, cy + Math.sin(a1) * r);
  }
}

/**
 * Solid polyline with a width at each vertex (round joins come from discs at the points).
 * `widths[i]` is the full width at vertex i.
 */
export function strokeVariable(pts: number[], widths: number[], closed: boolean, out: Tri): void {
  const n = pts.length / 2;
  if (n < 2) return;
  const p: number[] = [pts[0], pts[1]];
  const w: number[] = [Math.max(0, widths[0] ?? 0)];
  for (let i = 1; i < n; i++) {
    const x = pts[i * 2];
    const y = pts[i * 2 + 1];
    if (x !== p[p.length - 2] || y !== p[p.length - 1]) {
      p.push(x, y);
      w.push(Math.max(0, widths[Math.min(i, widths.length - 1)] ?? w[w.length - 1]));
    }
  }
  if (closed && p.length >= 6 && p[0] === p[p.length - 2] && p[1] === p[p.length - 1]) {
    p.length -= 2;
    w.length -= 1;
  }
  const m = p.length / 2;
  if (m < 2) return;
  const segs = closed ? m : m - 1;
  const nx: number[] = [];
  const ny: number[] = [];
  for (let i = 0; i < segs; i++) {
    const j = (i + 1) % m;
    const dx = p[j * 2] - p[i * 2];
    const dy = p[j * 2 + 1] - p[i * 2 + 1];
    const len = Math.hypot(dx, dy) || 1;
    nx.push(-dy / len);
    ny.push(dx / len);
  }
  const hw = (i: number) => w[i] / 2;
  for (let i = 0; i < segs; i++) {
    const j = (i + 1) % m;
    const ax = p[i * 2],
      ay = p[i * 2 + 1],
      bx = p[j * 2],
      by = p[j * 2 + 1];
    const ox0 = nx[i] * hw(i),
      oy0 = ny[i] * hw(i);
    const ox1 = nx[i] * hw(j),
      oy1 = ny[i] * hw(j);
    out.push(ax + ox0, ay + oy0, bx + ox1, by + oy1, bx - ox1, by - oy1);
    out.push(ax + ox0, ay + oy0, bx - ox1, by - oy1, ax - ox0, ay - oy0);
  }
  const joinCount = closed ? m : m - 2;
  for (let k = 0; k < joinCount; k++) {
    const i0 = closed ? (k - 1 + segs) % segs : k;
    const i1 = closed ? k : k + 1;
    const vi = closed ? k : k + 1;
    const vx = p[vi * 2],
      vy = p[vi * 2 + 1];
    const h = hw(vi);
    const cross = nx[i0] * ny[i1] - ny[i0] * nx[i1];
    if (Math.abs(cross) < 1e-9 && nx[i0] * nx[i1] + ny[i0] * ny[i1] > 0) continue;
    const s = cross > 0 ? -1 : 1;
    out.push(vx, vy, vx + nx[i0] * h * s, vy + ny[i0] * h * s, vx + nx[i1] * h * s, vy + ny[i1] * h * s);
  }
}

/** Same triangles, all counter-clockwise (for nonzero fills of a whole ribbon in one path). */
export function orientTris(tris: Tri): Tri {
  const out = tris.slice();
  for (let i = 0; i + 5 < out.length; i += 6) {
    const cross = (out[i + 2] - out[i]) * (out[i + 5] - out[i + 1]) - (out[i + 3] - out[i + 1]) * (out[i + 4] - out[i]);
    if (cross < 0) {
      out[i + 2] = tris[i + 4];
      out[i + 3] = tris[i + 5];
      out[i + 4] = tris[i + 2];
      out[i + 5] = tris[i + 3];
    }
  }
  return out;
}

/** Discs at vertices so a freehand stroke has round caps and joins. */
export function roundStroke(pts: number[], widths: number | number[], out: Tri, scale = 1): void {
  const n = pts.length / 2;
  if (n < 1) return;
  const wAt = (i: number) => (typeof widths === 'number' ? widths : (widths[Math.min(i, widths.length - 1)] ?? widths[0] ?? 0));
  const step = Math.max(1, Math.floor(n / 400));
  for (let i = 0; i < n; i += step) {
    const w = wAt(i);
    if (w > 0) disc(pts[i * 2], pts[i * 2 + 1], w / 2, out, w * scale > 6 ? 10 : 6);
  }
  const last = n - 1;
  const w = wAt(last);
  if (w > 0) disc(pts[last * 2], pts[last * 2 + 1], w / 2, out, w * scale > 6 ? 10 : 6);
}

/** Stroke with dash style. */
export function strokeStyled(pts: number[], width: number, dash: Dash, closed: boolean, out: Tri): void {
  if (dash === 'solid') {
    strokePolyline(pts, width, closed, out);
    return;
  }
  const parts = dashPolyline(pts, dashPattern(dash, width), closed);
  for (const part of parts) {
    const zeroLen = part.length === 4 && part[0] === part[2] && part[1] === part[3];
    if (dash === 'dotted' || zeroLen) {
      if (zeroLen || part.length >= 2) disc(part[0], part[1], width / 2, out, width > 6 ? 12 : 8);
    } else strokePolyline(part, width, false, out);
  }
}

/** Fill a simple polygon via earcut. */
export function fillPolygon(pts: number[], out: Tri, holes?: number[][]): void {
  if (!holes?.length) {
    const idx = earcut(pts);
    for (const i of idx) out.push(pts[i * 2], pts[i * 2 + 1]);
    return;
  }
  // earcut takes one flat array; hole rings start at the listed vertex indices
  const all = pts.slice();
  const starts: number[] = [];
  for (const h of holes) {
    starts.push(all.length / 2);
    for (const v of h) all.push(v);
  }
  const idx = earcut(all, starts);
  for (const i of idx) out.push(all[i * 2], all[i * 2 + 1]);
}
